import { ApolloError, useQuery } from '@apollo/client'
import * as R from 'ramda'
import { useMemo } from 'react'

import { GetCountriesDocument } from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { AddressRestrictions } from '../../cart/types'
import { SelectFieldOption } from '../../form'
import {
  countrySelectOptionsFor,
  RegionSelectFieldOption,
  regionSelectOptionsFor,
} from '../address'

const useCountriesAndRegions = (
  selectedCountryCode: unknown,
  addressRestrictions?: AddressRestrictions,
  includeCodeInLabel = false,
): {
  countryOptions: SelectFieldOption[]
  error: ApolloError | undefined
  loading: boolean
  regionOptions: RegionSelectFieldOption[]
} => {
  const { data, loading, error } = useQuery(GetCountriesDocument)
  useLogAndCaptureError(error)

  const countryOptions = useMemo(() => {
    const filterOptions = addressRestrictions
      ? R.filter(addressRestrictions.isCountryOptionAllowed)
      : R.identity
    return data?.countries ? filterOptions(countrySelectOptionsFor(data.countries)) : []
  }, [addressRestrictions, data?.countries])
  const regionOptions = useMemo(
    () =>
      data?.countries && typeof selectedCountryCode === 'string'
        ? regionSelectOptionsFor(data.countries, selectedCountryCode, includeCodeInLabel)
        : [],
    [data, includeCodeInLabel, selectedCountryCode],
  )
  return { countryOptions, error, loading, regionOptions }
}

export default useCountriesAndRegions
